import { lazy, Suspense, useEffect } from 'react';
import { AppHeader } from './components/AppHeader';
import { Button } from './components/ui/Button';
import { Toaster } from './components/ui/Toaster';
import { TooltipProvider } from './components/ui/Tooltip';
import { Spinner } from './components/ui/Spinner';
import { AuthProvider } from './lib/auth';
import { Home } from './pages/Home';
import { SignIn } from './pages/SignIn';
import { match, navigate, useLocation } from './router';

// The session room and administration load on demand; most visitors only need the home page
// first. The room is prefetched while the visitor fills in the code.
const loadMeeting = () => import('./pages/Meeting');
const MeetingPage = lazy(() => loadMeeting().then((m) => ({ default: m.MeetingPage })));
const Admin = lazy(() => import('./pages/Admin').then((m) => ({ default: m.Admin })));

function prefetchMeeting(): void {
  const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
  idle(() => void loadMeeting());
}

function PageLoading() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner className="size-5 text-ink-3" label="Loading" />
    </div>
  );
}

function Routes() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === '/') prefetchMeeting();
    if (pathname === '/') document.title = 'AMG Support';
    if (pathname === '/signin') document.title = 'Staff sign in · AMG Support';
  }, [pathname]);

  if (pathname === '/') return <Home />;
  if (pathname === '/signin') return <SignIn />;
  if (pathname === '/admin') return <Suspense fallback={<PageLoading />}><Admin /></Suspense>;
  const meeting = match('/m/:id', pathname);
  if (meeting?.id && /^[0-9a-f-]{36}$/i.test(meeting.id)) return <Suspense fallback={<PageLoading />}><MeetingPage key={meeting.id} meetingId={meeting.id} /></Suspense>;
  return <NotFound />;
}

function NotFound() {
  useEffect(() => {
    document.title = 'Page not found · AMG Support';
  }, []);
  return (
    <div className="flex min-h-dvh flex-col">
      <AppHeader />
      <main className="grid flex-1 place-items-center px-4">
        <div className="max-w-sm text-center">
          <p className="font-display text-[4rem] leading-none text-ink-4">404</p>
          <h1 className="mt-3 text-title font-semibold">This page doesn’t exist</h1>
          <p className="mt-2 text-compact text-ink-3">If someone sent you a link to a session, you can join from the home page with their code.</p>
          <Button className="mt-6" variant="primary" onClick={() => navigate('/')}>
            Go to the home page
          </Button>
        </div>
      </main>
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <TooltipProvider delayDuration={350} skipDelayDuration={150}>
        <Routes />
        <Toaster />
      </TooltipProvider>
    </AuthProvider>
  );
}
